import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';

const EMPTY = {
  id: null,
  nome: '',
  numero_instalacao_enel: '',
  endereco: '',
  data_inicio: '',
  ativa: true,
};

const SolarPowerPlantsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({
    search: '',
    status: 'TODAS',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/solar/power-plants');
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar usinas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredItems = useMemo(() => {
    const search = String(filters.search || '').trim().toLowerCase();
    return items.filter((item) => {
      if (filters.status === 'ATIVAS' && item.ativa !== true) return false;
      if (filters.status === 'INATIVAS' && item.ativa !== false) return false;
      if (!search) return true;

      const haystack = [item.nome, item.numero_instalacao_enel, item.endereco].map((value) => String(value || '').toLowerCase()).join(' ');
      return haystack.includes(search);
    });
  }, [filters.search, filters.status, items]);

  const openNew = () => {
    setForm(EMPTY);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      nome: item.nome || '',
      numero_instalacao_enel: item.numero_instalacao_enel || '',
      endereco: item.endereco || '',
      data_inicio: item.data_inicio || '',
      ativa: item.ativa !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      const payload = {
        nome: form.nome,
        numero_instalacao_enel: form.numero_instalacao_enel,
        endereco: form.endereco || null,
        data_inicio: form.data_inicio || null,
        ativa: form.ativa,
      };

      if (!payload.nome || !payload.numero_instalacao_enel) {
        toast.error('Nome e numero de instalacao ENEL sao obrigatorios');
        return;
      }

      if (form.id) {
        await axios.put(`/api/solar/power-plants/${form.id}`, payload);
        toast.success('Usina atualizada');
      } else {
        await axios.post('/api/solar/power-plants', payload);
        toast.success('Usina criada');
      }

      setShowModal(false);
      setForm(EMPTY);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar usina');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solar · Usinas</h1>
          <p className="text-gray-600">Cadastro de usinas geradoras</p>
        </div>
        <div className="flex gap-3 items-center">
          {loading && <div className="loader h-6 w-6" />}
          <button className="btn-primary" onClick={openNew}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Nova Usina
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3 border-b">
          <div className="md:col-span-2">
            <label className="form-label">Buscar</label>
            <input
              className="form-input"
              placeholder="Nome, instalacao ENEL ou endereco"
              value={filters.search}
              onChange={(e) => setFilters((old) => ({ ...old, search: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={filters.status} onChange={(e) => setFilters((old) => ({ ...old, status: e.target.value }))}>
              <option value="TODAS">Todas</option>
              <option value="ATIVAS">Ativas</option>
              <option value="INATIVAS">Inativas</option>
            </select>
          </div>
          <div className="flex items-end">
            <button className="btn-secondary w-full" onClick={() => setFilters({ search: '', status: 'TODAS' })}>
              Limpar
            </button>
          </div>
        </div>
        <div className="card-header">Usinas ({filteredItems.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Instalacao ENEL</th>
                  <th>Endereco</th>
                  <th>Inicio</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td className="font-medium">{item.nome}</td>
                    <td>{item.numero_instalacao_enel}</td>
                    <td>{item.endereco || '-'}</td>
                    <td>{item.data_inicio || '-'}</td>
                    <td>
                      <span className={item.ativa ? 'badge-success' : 'badge-danger'}>{item.ativa ? 'Ativa' : 'Inativa'}</span>
                    </td>
                    <td>
                      <button className="text-primary-600 hover:text-primary-900 p-1" onClick={() => openEdit(item)}>
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500">
                      Nenhuma usina cadastrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">{form.id ? 'Editar Usina' : 'Nova Usina'}</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Nome</label>
                <input className="form-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Numero Instalacao ENEL</label>
                <input
                  className="form-input"
                  value={form.numero_instalacao_enel}
                  onChange={(e) => setForm({ ...form, numero_instalacao_enel: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Data Inicio</label>
                <input className="form-input" type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Endereco</label>
                <input className="form-input" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.ativa} onChange={(e) => setForm({ ...form, ativa: e.target.checked })} />
                  Usina ativa
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={save}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarPowerPlantsPage;

